/**
 * Choosing a date input without changing the value's shape.
 *
 * ADI licensing windows arrive in more than one shape — `2024-04-01T09:00:00` in the real NBCU
 * packages, bare `2026-08-01` in others — and VMS must not alter ingested data. So the control has to
 * adapt to the value rather than normalising the value to suit the control: a date-only field edited
 * through a datetime input would gain a `T00:00:00` nobody asked for, and every item would show a diff.
 *
 * The other requirement is that a value we do not recognise stays editable as text. A licensing window
 * in the demo environment had been corrupted to `2024-04-01T0dff9:00:00` by hand-typing; a date input
 * would render that as empty, so the operator could neither see the problem nor fix it, and "empty"
 * would then be written back as a cleared override.
 *
 * Native inputs rather than a picker library, deliberately. `datetime-local` and `date` carry no
 * timezone, so there is no conversion to get wrong — a picker that helpfully normalised to UTC would
 * shift every window in the catalogue. They also cost nothing in a bundle already large enough to warn.
 */

export type DateShape = 'date' | 'datetime' | 'datetimeSeconds' | 'unknown';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_MINUTES = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}$/;
const DATE_SECONDS = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}$/;

/** Empty counts as `date`: nothing to preserve, and a picker is the friendlier default. */
export const detectShape = (value: string): DateShape => {
  if (value === '') {
    return 'date';
  }
  if (DATE_ONLY.test(value)) {
    return 'date';
  }
  if (DATE_SECONDS.test(value)) {
    return 'datetimeSeconds';
  }
  if (DATE_MINUTES.test(value)) {
    return 'datetime';
  }
  return 'unknown';
};

/** The `type` attribute for a shape. `unknown` stays text so a malformed value remains visible. */
export const inputType = (
  shape: DateShape,
): 'date' | 'datetime-local' | 'text' => {
  switch (shape) {
    case 'date':
      return 'date';
    case 'datetime':
    case 'datetimeSeconds':
      return 'datetime-local';
    default:
      return 'text';
  }
};

/**
 * A native input needs `T` as the separator, so a value using a space is rewritten for display only.
 * `fromInput` puts the original separator back.
 */
export const toInput = (value: string, shape: DateShape): string =>
  shape === 'unknown' ? value : value.replace(' ', 'T');

/**
 * The value to store, given what the input produced.
 *
 * Two shape corrections. A `datetime-local` input drops `:00` seconds even when `step` asks for them, so
 * a value that arrived with seconds gets them back rather than being shortened. And the separator the
 * provider used is restored, because rewriting a space to `T` would be an edit to a field the operator
 * only looked at.
 */
export const fromInput = (
  inputValue: string,
  original: string,
  shape: DateShape,
): string => {
  if (shape === 'unknown' || inputValue === '') {
    return inputValue;
  }

  const separator =
    original.includes(' ') && !original.includes('T') ? ' ' : 'T';
  let next = inputValue.replace('T', separator);

  if (shape === 'datetimeSeconds' && DATE_MINUTES.test(next)) {
    next = `${next}:00`;
  }

  return next;
};

/**
 * The property this exists to guarantee: displaying a value and reading it back unchanged must return
 * exactly what went in. Anything else means opening the Availability tab silently edits the item.
 */
export const roundTrips = (value: string): boolean => {
  const shape = detectShape(value);
  return fromInput(toInput(value, shape), value, shape) === value;
};
